import React, { useState, useEffect } from "react";
import {
  Typography,
  Box,
  Skeleton,
  Divider,
  TextField,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  Checkbox,
  Alert,
  CircularProgress,
  Button,
  styled,
} from "@mui/material";
import { useLocation } from "react-router-dom";
import { isMobile } from "react-device-detect";
import logoImage from "../../../assets/images/logo.png";
import callApi from "../../../services/callApi";
import Loader from "../../../components/Loader/Loader";

// Styled components for form - COPIADO EXACTO DE Ingresada.jsx
const QuestionContainer = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(4),
  padding: theme.spacing(2),
  borderRadius: 8,
  border: "1px solid #e0e0e0",
  position: "relative",
  overflowWrap: "break-word",
  wordWrap: "break-word",
  wordBreak: "break-word",
  hyphens: "auto",
}));

const RequiredIndicator = styled(Box)({
  color: "#FF5252",
  fontWeight: "bold",
  display: "inline",
});

const SubmitButton = styled(Button)(({ theme }) => ({
  fontSize: { xs: "14px", md: "16px", lg: "16px" },
  fontFamily: "Quicksand-Bold",
  backgroundColor: "#3B82F6",
  "&:hover": { 
    backgroundColor: "#3B82F6",
    boxShadow: "none !important",
  },
  "&:active": {
    backgroundColor: "#3B82F6",
    boxShadow: "none !important",
  },
  "&:focus": {
    backgroundColor: "#3B82F6",
    boxShadow: "none !important",
  },
  borderRadius: 5,
  width: "100%",
  color: "white",
  textTransform: "none",
  boxShadow: "none !important",
  transition: "none !important",
  "& .MuiTouchRipple-root": {
    display: "none",
  },
  "&.Mui-disabled": {
    backgroundColor: "#E5E7EB",
    color: "#9CA3AF",
    boxShadow: "none !important",
  },
}));

const MAX_CHAR_LIMIT = 1500;

export default function ExitoTransferencia() {
  const location = useLocation();

  // Estados para el componente ExitoTransferencia
  const [transferenciaData, setTransferenciaData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [formularioEnviado, setFormularioEnviado] = useState(false);

  // Estados para el formulario integrado
  const [respuestas, setRespuestas] = useState({});
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState(null);
  const [exito, setExito] = useState(false);

  useEffect(() => {
    // Scroll instantáneo a la parte superior
    window.scrollTo(0, 0);
  }, []);


  useEffect(() => {
    getTransferenciaDetails();
  }, []);

  async function getTransferenciaDetails() {
    if (!location.state || !location.state.transferencia_id) {
      console.error("No transferencia_id found in location state");
      setLoading(false);
      return;
    }

    try {
      const response = await callApi(
        `/agenda/agendarExitoTransferencia?transferencia_id=${location.state.transferencia_id}`
      );
      setTransferenciaData(response.data);
      
      // Inicializar respuestas del formulario
      if (response.data.formulario && response.data.formulario.formulario.preguntas) {
        const respuestasIniciales = {};
        response.data.formulario.formulario.preguntas.forEach((pregunta, index) => {
          if (pregunta.tipo_pregunta === "seleccion_multiple") {
            respuestasIniciales[`pregunta_${index}`] = [];
          } else {
            respuestasIniciales[`pregunta_${index}`] = "";
          }
        });
        setRespuestas(respuestasIniciales);
      }
    } catch (error) {
      console.error("Error fetching transferencia details:", error);
    } finally {
      setLoading(false);
    }
  }

  const formatDate = (dateString) => {
    if (!dateString) return "";

    const date = new Date(dateString + "T00:00:00");
    const days = [
      "domingo",
      "lunes",
      "martes",
      "miércoles",
      "jueves",
      "viernes",
      "sábado",
    ];
    const months = [
      "enero",
      "febrero",
      "marzo",
      "abril",
      "mayo",
      "junio",
      "julio",
      "agosto",
      "septiembre",
      "octubre",
      "noviembre",
      "diciembre",
    ];

    const dayName = days[date.getDay()];
    const day = date.getDate().toString().padStart(2, "0");
    const month = months[date.getMonth()];
    return `${dayName} ${day} de ${month}`;
  };

  const transformModalidad = (modalidad) => {
    switch (modalidad) {
      case "consultorio":
        return "Consulta presencial";
      case "domicilio":
        return "Consulta a domicilio";
      case "online":
        return "Consulta online";
      case "telemedicina":
        return "Consulta online";
      default:
        return modalidad;
    }
  };

  // Funciones del formulario integrado
  const handleTextChange = (event, index) => {
    const value = event.target.value;

    if (value.length <= MAX_CHAR_LIMIT) {
      setRespuestas({
        ...respuestas,
        [`pregunta_${index}`]: value,
      });
    }
  };

  const handleMultipleChange = (event, index, opcion) => {
    const isChecked = event.target.checked;
    const currentResponses = respuestas[`pregunta_${index}`] || [];
    const opcionId = opcion.orden;

    let newResponses;
    if (isChecked) {
      newResponses = [...currentResponses, opcionId];
    } else {
      newResponses = currentResponses.filter((item) => item !== opcionId);
    }

    setRespuestas({
      ...respuestas,
      [`pregunta_${index}`]: newResponses,
    });
  };

  const isOpcionSeleccionada = (index, opcion) => {
    const respuesta = respuestas[`pregunta_${index}`];
    if (!Array.isArray(respuesta)) return false;
    return respuesta.includes(opcion.orden);
  };

  const isPreguntaRespondida = (index, pregunta) => {
    const respuesta = respuestas[`pregunta_${index}`];

    if (pregunta.tipo_pregunta === "seleccion_multiple") {
      return Array.isArray(respuesta) && respuesta.length > 0;
    } else if (pregunta.tipo_pregunta === "seleccion_unica") {
      return respuesta !== undefined && respuesta !== "";
    }

    return respuesta && respuesta.trim() !== "";
  };

  const getFormularioValidationAlert = () => {
    if (!transferenciaData?.formulario?.formulario?.preguntas) return "";
    
    for (let index = 0; index < transferenciaData.formulario.formulario.preguntas.length; index++) {
      const pregunta = transferenciaData.formulario.formulario.preguntas[index];
      
      if (pregunta.obligatorio && !isPreguntaRespondida(index, pregunta)) {
        return `Completa la pregunta ${index + 1}: ${pregunta.texto_pregunta}`;
      }
    }
    
    return "";
  };
  
  const isFormularioValido = () => {
    return getFormularioValidationAlert() === "";
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();

    if (!isFormularioValido()) {
      setError("Por favor, completa todas las preguntas obligatorias");
      return;
    }

    try {
      setEnviando(true);
      setError(null);

      // Formatear respuestas para enviar a la API
      const preguntasConRespuestas = transferenciaData.formulario.formulario.preguntas.map(
        (pregunta, index) => {
          const respuestaPregunta = respuestas[`pregunta_${index}`];

          const preguntaFormateada = {
            texto_pregunta: pregunta.texto_pregunta,
            tipo_pregunta: pregunta.tipo_pregunta,
            obligatorio: pregunta.obligatorio ? 1 : 0,
            orden: index + 1,
          };

          if (pregunta.tipo_pregunta === "abierta") {
            preguntaFormateada.texto_respuesta = respuestaPregunta;
          } else if (pregunta.tipo_pregunta === "seleccion_unica") {
            preguntaFormateada.opciones = pregunta.opciones.map((opcion) => {
              return {
                texto_opcion: opcion.texto_opcion,
                orden: opcion.orden,
                seleccionada: parseInt(respuestaPregunta) === opcion.orden,
              };
            });
          } else if (pregunta.tipo_pregunta === "seleccion_multiple") {
            preguntaFormateada.opciones = pregunta.opciones.map((opcion) => {
              return {
                texto_opcion: opcion.texto_opcion,
                orden: opcion.orden,
                seleccionada: respuestaPregunta.includes(opcion.orden),
              };
            });
          }

          return preguntaFormateada;
        }
      );

      const datosEnvio = {
        id_formulario_asignado: transferenciaData.formulario.id_formulario_asignado,
        formulario: {
          nombre_formulario: transferenciaData.formulario.formulario.nombre_formulario,
          descripcion_formulario: transferenciaData.formulario.formulario.descripcion_formulario,
          preguntas: preguntasConRespuestas,
        },
      };

      if (transferenciaData.id_intencion) {
        datosEnvio.id_intencion = transferenciaData.id_intencion;
      }

      console.log("Datos a enviar:", JSON.stringify(datosEnvio, null, 2));

      const response = await callApi(
        "/formulario/formularioGuardar",
        "post",
        datosEnvio
      );

      if (response?.statusCode === 200) {
        setExito(true);
        setFormularioEnviado(true);
      } else {
        setError(
          response?.message ||
            "No se pudieron guardar tus respuestas. Por favor, intenta nuevamente."
        );
      }
    } catch (error) {
      console.error("Error al enviar respuestas:", error);
      const errorMsg =
        error.response?.data?.message ||
        "Hubo un error al enviar tus respuestas. Por favor, intenta nuevamente.";
      setError(errorMsg);
    } finally {
      setEnviando(false);
    }
  };

  return (
    <>
      <Loader isLoading={loading} />
      {/* LOGO - COPIADO EXACTO DE Ingresada.jsx */}
      <img
        src={logoImage}
        alt="Clinifica Logo"
        style={{
          width: isMobile ? "150px" : "200px",
          height: "auto",
          paddingTop: "15px",
          display: "block",
          marginLeft: "auto",
          marginRight: "auto",
          padding: 15,
        }}
      />
      
      {/* CAJA MORADA SUPERIOR - ESTRUCTURA EXACTA DE Ingresada.jsx */}
      <Box
        sx={{
          maxWidth: isMobile ? "94%" : "800px",
          width: isMobile ? "94%" : "90%",
          mx: "auto",
          bgcolor: "#fbfafe",
          boxShadow: 1,
          marginTop: 2,
          marginBottom: 2,
          borderRadius: 2,
          p: isMobile ? 2 : 4,
        }}
      >
        {/* TEXTO DESCRIPTIVO - COPIADO EXACTO DE Ingresada.jsx */}
        <Typography sx={{ 
          fontFamily: "Quicksand-Regular",
          color: "#626262",
          fontSize: { xs: "14px", md: "16px" },
          mb: 0
        }}>
          Pago confirmado para:
        </Typography>
        
        {loading ? (
          <>
            <Skeleton
              animation="wave"
              height={30}
              width="80%"
            />
            <Skeleton
              animation="wave"
              height={20}
              width="60%"
            />
            <Skeleton
              animation="wave"
              height={30}
              width="70%"
            />
            <Skeleton
              animation="wave"
              height={20}
              width="40%"
            />
            <Skeleton
              animation="wave"
              height={20}
              width="50%"
            />
            <Skeleton
              animation="wave"
              height={25}
              width="30%"
            />
            <Skeleton
              animation="wave"
              height={25}
              width="50%"
            />
          </>
        ) : transferenciaData ? (
          <>
            {/* NOMBRE PROFESIONAL - COPIADO EXACTO DE Ingresada.jsx */}
            <Typography
              sx={{
                mt: -0.5,
                fontFamily: "Quicksand-Bold",
                fontSize: { xs: "18px", md: "20px" },
                color: "#191C1F",
                mb: 0
              }}
            >
              {transferenciaData.profesional_nombre_plataforma}
            </Typography>
            
            {/* PROFESIÓN - COPIADO EXACTO DE Ingresada.jsx */}
            <Typography
              sx={{ 
                fontFamily: "Quicksand-Regular", 
                mt: -0.5,
                fontSize: { xs: "14px", md: "16px" },
                mb: 0
              }}
            >
              {transferenciaData.profesional_profesion}
            </Typography>
            
            {/* TEXTO SERVICIO - COPIADO EXACTO DE Ingresada.jsx */}
            <Typography sx={{ 
              fontFamily: "Quicksand-Regular",
              color: "#626262",
              mt: 2,
              fontSize: { xs: "14px", md: "16px" },
              mb: 0
            }}>
              El servicio de:
            </Typography>
            
            {/* NOMBRE SERVICIO - COPIADO EXACTO DE Ingresada.jsx */}
            <Typography
              sx={{
                mt: -0.5,
                fontFamily: "Quicksand-Bold",
                fontSize: { xs: "18px", md: "20px" },
                color: "#191C1F",
                mb: 0
              }}
            >
              {transferenciaData.servicio_nombre}
            </Typography>
            
            {/* PRECIO - COPIADO EXACTO DE Ingresada.jsx */}
            <Typography
              sx={{ 
                fontFamily: "Quicksand-Regular", 
                mt: -0.5,
                fontSize: { md: "16px", xs: "14px" },
                color: "#191C1F",
                mb: 0
              }}
            >
              {transferenciaData.servicio_precio}
            </Typography>
            
            {/* CHIP MODALIDAD - COPIADO EXACTO DE Ingresada.jsx */}
            <Box
              sx={{
                display: "inline-block",
                backgroundColor: "#f0f4ff",
                color: "#3B82F6",
                fontFamily: "Quicksand-Medium",
                fontSize: { md: "15px", xs: "13px" },
                borderRadius: 2,
                paddingLeft: 0.75,
                paddingRight: 0.75,
                paddingTop: 0.25,
                paddingBottom: 0.25,
                marginLeft: -0.75,
                mt: -0.75,
                textAlign: "left",
              }}
            >
              {transformModalidad(transferenciaData.servicio_modalidad)}
            </Box>
            
            {/* FECHA Y HORA - COPIADO EXACTO DE Ingresada.jsx */}
            <Typography
              sx={{
                mt: 0,
                color: "#007FFF",
                fontFamily: "Quicksand-Bold",
                fontSize: { md: "16px", xs: "14px" },
              }}
            >
              {formatDate(transferenciaData.fecha_agenda)} -{" "}
              {transferenciaData.hora_agenda}
            </Typography>
          </>
        ) : (
          <Typography sx={{ fontFamily: "Quicksand-Regular" }}>
            No se pudo cargar la información de la transferencia.
          </Typography>
        )}
      </Box>
      
      {/* TÍTULO PRINCIPAL - COPIADO EXACTO DE Ingresada.jsx */}
      <Typography
        sx={{
          fontFamily: "Quicksand-Bold",
          fontSize: { md: "24px", xs: "20px" },
          textAlign: "center",
          color: "#191C1F",
          mt: 3,
          mb: 1
        }}
      >
        Transferencia confirmada
      </Typography>
      
      {/* CAJA BLANCA PRINCIPAL - ESTRUCTURA EXACTA DE Ingresada.jsx */}
      <Box
        sx={{
          maxWidth: isMobile ? "94%" : "800px",
          width: isMobile ? "94%" : "90%",
          mx: "auto",
          bgcolor: "white",
          boxShadow: 1,
          marginTop: 2,
          marginBottom: 8,
          borderRadius: 2,
          p: isMobile ? 2 : 4,
        }}
      >
        {/* MENSAJE PERSONALIZADO - COPIADO ESTRUCTURA DE Ingresada.jsx */}
        {loading ? (
          <Skeleton
            animation="wave"
            height={34}
            width="90%"
            sx={{ borderRadius: 1 }}
          />
        ) : (
          <Typography
            sx={{
              fontFamily: "Quicksand-Regular",
              fontSize: { xs: "14px", md: "16px" },
              color: "#191C1F",
              textAlign: "left",
              lineHeight: 1.6,
              mb: 3,
            }}
          >
        <Box component="span" sx={{ fontWeight: "bold" }}>
          {transferenciaData?.paciente_nombre}
        </Box>
        , hemos procesado tu transferencia y enviado un correo con la confirmacion de cita a a{" "}
        <Box component="span" sx={{ fontWeight: "bold" }}>
          {transferenciaData?.paciente_email}
        </Box>
          </Typography>
        )}

        {/* Formulario integrado */}
        {!loading &&
          transferenciaData &&
          transferenciaData.formulario &&
          transferenciaData.formulario.formulario.estado !== "completado" &&
          !formularioEnviado && (
            <>
              <Divider sx={{ my: 3 }} />

              {/* TEXTO IMPORTANTE - ALINEADO A LA IZQUIERDA */}
              <Typography
                variant="body1"
                sx={{
                  mb: 3,
                  mt: 3,
                  fontFamily: "Quicksand-Regular",
                  fontSize: { xs: "14px", md: "16px" },
                  textAlign: "left",
                  position: "relative",
                  background: "linear-gradient(90deg, #191C1F 25%, #d0d0d0 50%, #191C1F 75%)",
                  backgroundSize: "200% 100%",
                  backgroundClip: "text",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  animation: "shimmer 2.0s ease-in-out infinite",
                  "@keyframes shimmer": {
                    "0%": {
                      backgroundPosition: "100% 0",
                    },
                    "100%": {
                      backgroundPosition: "-100% 0",
                    },
                  },
                }}
                >
                <Box component="span" sx={{ fontWeight: "bold" }}>
                  IMPORTANTE
                </Box>
                , el profesional necesita que responda el siguiente
                formulario para preparar la sesión
              </Typography>

              {/* TÍTULO FORMULARIO - CENTRADO */}
              <Typography
                variant="h6"
                sx={{
                  fontFamily: "Quicksand-Bold",
                  fontSize: { xs: "18px", md: "20px" },
                  color: "#191C1F",
                  textAlign: "center",
                  mb: 2,
                }}
              >
                {transferenciaData.formulario.formulario.nombre_formulario}
              </Typography>

              {/* DESCRIPCIÓN - ALINEADA A LA IZQUIERDA */}
              {transferenciaData.formulario.formulario.descripcion_formulario && (
                <Typography
                  variant="body2"
                  sx={{
                    fontFamily: "Quicksand-Regular",
                    fontSize: { xs: "14px", md: "16px" },
                    color: "#191C1F",
                    textAlign: "left",
                    lineHeight: 1.6,
                    mb: 3,
                  }}
                >
                  {transferenciaData.formulario.formulario.descripcion_formulario}
                </Typography>
              )}

              {error && (
                <Alert
                  severity="error"
                  sx={{
                    mb: 3,
                    borderRadius: 2,
                  }}
                >
                  {error}
                </Alert>
              )}

              {/* Mapeo de preguntas - USANDO STYLED COMPONENTS DE Ingresada.jsx */}
              {transferenciaData.formulario.formulario.preguntas?.map((pregunta, index) => (
                <QuestionContainer key={index}>
                  <Typography
                    sx={{
                      fontFamily: "Quicksand-Regular",
                      fontSize: { xs: "14px", md: "16px" },
                      color: "#191C1F",
                      mb: 2,
                    }}
                  >
                    {index + 1}. {pregunta.texto_pregunta}
                  </Typography>

                  {(pregunta.tipo_pregunta === "abierta" || pregunta.tipo_pregunta === "texto_corto" || pregunta.tipo_pregunta === "texto_largo") && (
                    <>
                      <TextField
                        fullWidth
                        multiline={pregunta.tipo_pregunta === "texto_largo" || pregunta.tipo_pregunta === "abierta"}
                        rows={pregunta.tipo_pregunta === "texto_largo" || pregunta.tipo_pregunta === "abierta" ? 4 : 1}
                        placeholder="Escribe tu respuesta aquí"
                        variant="outlined"
                        value={respuestas[`pregunta_${index}`] || ""}
                        onChange={(e) => handleTextChange(e, index)}
                        inputProps={{ maxLength: MAX_CHAR_LIMIT }}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: 2,
                            fontFamily: "Quicksand-Regular",
                            fontSize: { xs: "14px", md: "16px" },
                            "& input": {
                              color: "#4F4F4F",
                            },
                            "& textarea": {
                              color: "#4F4F4F",
                            },
                            "& .MuiInputBase-input::placeholder": {
                              color: "#9CA3AF",
                              opacity: 1,
                            },
                          },
                        }}
                      />
                    </>
                  )}

                  {pregunta.tipo_pregunta === "seleccion_unica" && (
                    <FormControl
                      component="fieldset"
                      fullWidth
                    >
                      <RadioGroup value={respuestas[`pregunta_${index}`] || ""}>
                        {pregunta.opciones?.map((opcion, opcionIndex) => (
                          <FormControlLabel
                            key={opcionIndex}
                            value={opcion.orden.toString()}
                            control={
                              <Radio
                                checked={
                                  parseInt(respuestas[`pregunta_${index}`]) ===
                                  opcion.orden
                                }
                                onChange={() => {
                                  setRespuestas({
                                    ...respuestas,
                                    [`pregunta_${index}`]: opcion.orden.toString(),
                                  });
                                }}
                                sx={{
                                  color: "#3B82F6",
                                  "&.Mui-checked": {
                                    color: "#3B82F6",
                                  },
                                }}
                              />
                            }
                            label={
                              <Typography
                                sx={{
                                  fontFamily: "Quicksand-Regular",
                                  fontSize: { xs: "14px", md: "16px" },
                                  color: "#191C1F",
                                }}
                              >
                                {opcion.texto_opcion}
                              </Typography>
                            }
                          />
                        ))}
                      </RadioGroup>
                    </FormControl>
                  )}

                  {pregunta.tipo_pregunta === "seleccion_multiple" && (
                    <FormControl
                      component="fieldset"
                      fullWidth
                    >
                      {pregunta.opciones?.map((opcion, opcionIndex) => (
                        <FormControlLabel
                          key={opcionIndex}
                          control={
                            <Checkbox
                              checked={isOpcionSeleccionada(index, opcion)}
                              onChange={(e) => handleMultipleChange(e, index, opcion)}
                              sx={{
                                color: "#3B82F6",
                                "&.Mui-checked": {
                                  color: "#3B82F6",
                                },
                              }}
                            />
                          }
                          label={
                            <Typography
                              sx={{
                                fontFamily: "Quicksand-Regular",
                                fontSize: { xs: "14px", md: "16px" },
                                color: "#191C1F",
                              }}
                            >
                              {opcion.texto_opcion}
                            </Typography>
                          }
                        />
                      ))}
                    </FormControl>
                  )}
                </QuestionContainer>
              ))}

              {/* BOTÓN ENVIAR - USANDO STYLED COMPONENT DE Ingresada.jsx */}
              {getFormularioValidationAlert() && (
                <Box
                  sx={{
                    backgroundColor: "#FFF9E6",
                    padding: 1,
                    borderRadius: 2,
                    mb: 2,
                    textAlign: "center",
                    width: "100%",
                    boxSizing: "border-box",
                  }}
                >
                  <Typography
                    sx={{
                      fontFamily: "Quicksand-Regular",
                      fontSize: { md: "14px", xs: "12px" },
                      color: "#836516",
                    }}
                  >
                    {getFormularioValidationAlert()}
                  </Typography>
                </Box>
              )}

              <SubmitButton
                onClick={handleSubmit}
                disabled={enviando || !isFormularioValido()}
                disableRipple
                disableElevation
                disableFocusRipple
                variant="contained"
              >
                {enviando ? <CircularProgress size={24} color="inherit" /> : "ENVIAR"}
              </SubmitButton>
            </>
          )}

        {/* Mensaje de éxito cuando el formulario ha sido enviado */}
        {(formularioEnviado || exito) && (
          <>
            <Divider sx={{ my: 3 }} />
            <Box
              sx={{
                width: "100%",
                backgroundColor: "#e8f5e9",
                borderRadius: 2,
                display: "flex",
                alignItems: "center",
                mb: 3,
              }}
            >
              <Typography
                sx={{
                  fontFamily: "Quicksand-Regular",
                  color: "#2e7d32",
                  p: 2,
                  m: "auto",
                  textAlign: "left",
                }}
              >
                ✅ ¡Gracias por completar el formulario! Tus respuestas han
                sido enviadas correctamente.
              </Typography>
            </Box>
          </>
        )}
      </Box>
    </>
  );
}

